// One place that turns a document + generation result into a saved file, so
// the export dialog, toolbar, and command palette cannot drift apart.

import type { DocumentModel, GenResult } from '../core/types'
import { exportSvg } from '../export/svg'
import { exportStl } from '../export/stl'
import { exportStep } from '../export/step'
import { exportDxf } from '../export/dxf'
import { exportJson, parseDocumentFile } from '../export/json'
import { saveBlob, saveText } from './download'

export type ExportFormat = 'svg' | 'dxf' | 'stl' | 'step' | 'stepShapes' | 'stepSplit' | 'json'

export const FORMAT_INFO: Record<
  ExportFormat,
  { name: string; ext: string; desc: string; needsResult: boolean }
> = {
  svg: {
    name: 'SVG',
    ext: '.svg',
    desc: 'Vector outlines in mm for laser cutting and design tools. Embeds the full design for drag-back restore.',
    needsResult: true,
  },
  dxf: {
    name: 'DXF',
    ext: '.dxf',
    desc: 'CNC and sheet-metal outlines (R12, mm). Circles stay CIRCLE entities; holes and boundary on separate layers.',
    needsResult: true,
  },
  stl: {
    name: 'STL',
    ext: '.stl',
    desc: 'Watertight mesh of the panel, ready to slice in PrusaSlicer or Cura.',
    needsResult: true,
  },
  step: {
    name: 'STEP panel',
    ext: '.step',
    desc: 'Panel solid for CAD. Untouched circular holes stay true cylinders.',
    needsResult: true,
  },
  stepShapes: {
    name: 'STEP shapes',
    ext: '.step',
    desc: 'Each hole as its own solid, for cutting or patterning inside your CAD.',
    needsResult: true,
  },
  stepSplit: {
    name: 'STEP split',
    ext: '.step',
    desc: 'The panel cut into aligned sub-panels (absolute coordinates preserved) so dense patterns open fast in CAD.',
    needsResult: true,
  },
  json: {
    name: 'Design file',
    ext: '.json',
    desc: 'The complete document. Drop it back on the app to restore this design.',
    needsResult: false,
  },
}

// Writes the file and returns its name, or null when a result is required but
// not available yet.
export function runExport(
  format: ExportFormat,
  doc: DocumentModel,
  result: GenResult | null,
): string | null {
  if (FORMAT_INFO[format].needsResult && !result) return null
  switch (format) {
    case 'svg':
      saveText('pattern.svg', exportSvg(doc, result!), 'image/svg+xml')
      return 'pattern.svg'
    case 'dxf':
      saveText('pattern.dxf', exportDxf(result!, doc.export.dxfBoundary), 'application/dxf')
      return 'pattern.dxf'
    case 'stl':
      saveBlob('panel.stl', new Blob([exportStl(result!, doc.export.thickness)], { type: 'model/stl' }))
      return 'panel.stl'
    case 'step':
      saveText('panel.step', exportStep(result!, doc.export.thickness, doc.export.tolerance), 'application/step')
      return 'panel.step'
    case 'stepShapes':
      saveText(
        'holes.step',
        exportStep(result!, doc.export.thickness, doc.export.tolerance, 'shapes'),
        'application/step',
      )
      return 'holes.step'
    case 'stepSplit':
      saveText(
        'panel-split.step',
        exportStep(result!, doc.export.thickness, doc.export.tolerance, 'panel', {
          cols: doc.export.splitCols,
          rows: doc.export.splitRows,
        }),
        'application/step',
      )
      return 'panel-split.step'
    case 'json':
      saveText('design.json', exportJson(doc), 'application/json')
      return 'design.json'
  }
}

// Opens a file picker for a design file (JSON or exported SVG).
export function pickDesignFile(onLoaded: (doc: DocumentModel) => void, onFail: () => void) {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = '.json,.svg,application/json,image/svg+xml'
  input.onchange = async () => {
    const file = input.files?.[0]
    if (!file) return
    const text = await file.text()
    const parsed = parseDocumentFile(text)
    if (parsed) onLoaded(parsed)
    else onFail()
  }
  input.click()
}
